import { animate, state, style, transition, trigger } from '@angular/animations';
import { ScaleType } from '@swimlane/ngx-charts';
import { FilterType, LocationType, TableDataType } from './enums/common.enums';
import {
  ACCOUNT_STATUS_META,
  BUSINESS_TYPE_META,
  CAPACITY_RANGE_META,
  CUSTOMER_DROPOFF_STATUS_META,
  LEAD_STATUS_META,
  LOCATION_STATUS_META,
  PACKAGE_STATUS_META,
  SHIPMENT_STATUS_META,
  SUBSCRIPTION_STATUS_META,
  SUBSCRIPTION_TYPE_META,
  statusOptions,
  statusOptionsString,
} from './utilities/status.meta';
import { TableColumn } from './models/common.model';

export const ResponsiveRanges = {
  mobile: 768,
  tablet: 1024,
  largeDesktop: 1200,
};

export const Animations = {
  ExpandAnimation: trigger('detailExpand', [
    state('collapsed', style({ height: '0px', minHeight: '0' })),
    state('expanded', style({ height: '*' })),
    transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
  ]),
  OpenClose: trigger('openClose', [
    state('open', style({ 'margin-left': '281px' })),
    state('closed', style({ 'margin-left': '71px' })),
    state('none', style({ 'margin-left': '0px' })),
    transition('open <=> closed', animate('0.25s ease-in-out')),
    transition('open <=> none', animate('0.25s ease-in-out')),
    transition('closed <=> none', animate('0.25s ease-in-out')),
  ]),
  RotateIcon: trigger('rotateIcon', [
    // ...
    state(
      'open',
      style({
        rotate: '0deg',
      }),
    ),
    state(
      'closed',
      style({
        rotate: '180deg',
      }),
    ),
    transition('open => closed', [animate('0.25s ease-in-out')]),
    transition('closed => open', [animate('0.25s ease-in-out')]),
  ]),
};

export const SUPER_ADMIN = 'Super Admin';
export const ADMIN = 'Admin';
export const HOST_AGENT = 'Host Agent';
export const CORPORATE_ADMIN = 'Corporate Admin';

// Eastern Ave and University Ave do not receive partner commissions.
export const COMMISSION_EXCLUDED_LOCATION_CODES: readonly string[] = ['PPU#004', 'PPU#028'];

export const PurolatorID: number = 1;
export const FedExID: number = 3;
export const DHLID: number = 5;
export const CanparID: number = 12;
export const NationexID: number = 47;
export const ICSCourierID: number = 59;
export const PickPackID: number = 62;
export const GeaZoneID: number = 63;

// Courier name constants
export const PUROLATOR_NAME = 'purolator';
export const FEDEX_NAME = 'fedex';
export const DHL_NAME = 'dhl';
export const CANPAR_NAME = 'canpar';
export const NATIONEX_NAME = 'nationex';
export const ICS_COURIER_NAME = 'icsCourier';
export const PICKPACK_NAME = 'pickpack';
export const GEAZONE_NAME = 'geazone';

// Courier display names for templates
export const PUROLATOR_DISPLAY_NAME = 'Purolator';
export const FEDEX_DISPLAY_NAME = 'FedEx';
export const DHL_DISPLAY_NAME = 'DHL';
export const CANPAR_DISPLAY_NAME = 'Canpar';
export const NATIONEX_DISPLAY_NAME = 'Nationex';
export const ICS_COURIER_DISPLAY_NAME = 'ICS Courier';
export const PICKPACK_DISPLAY_NAME = 'PickPack';
export const GEAZONE_DISPLAY_NAME = 'GeaZone';

export const DropoffTableColumns = new Map([
  ['trackingID', { label: 'App.Dropoff.Table.TrackingID', format: TableDataType.Text }],
  ['carrier', { label: 'App.Dropoff.Table.Carrier', format: TableDataType.Text }],
  ['createdOn', { label: 'App.Dropoff.Table.CreatedOn', format: TableDataType.LongDate }],
  ['dropoffPackageStatusId', { label: 'App.Dropoff.Table.Status', isStatusColumn: true }],
  ['pickupDate', { label: 'App.Dropoff.Table.PickedOn', format: TableDataType.LongDate }],
]);

let dropoffCourierOptions = [
  { id: 1, value: 'Purolator' },
  { id: 5, value: 'DHL' },
  { id: 12, value: 'Canpar' },
  { id: 59, value: 'ICS Courier' },
  { id: 62, value: 'PickPack' },
  { id: 63, value: 'FedEx' },
];

export const DropoffFilters = new Map([
  ['trackingID', { options: [], type: FilterType.Text }],
  [
    'courier',
    {
      options: dropoffCourierOptions,
      type: FilterType.MultiSelect,
    },
  ],
  ['createdOn', { options: [], type: FilterType.DateRange }],
  [
    'statusId',
    {
      options: statusOptions(CUSTOMER_DROPOFF_STATUS_META),
      type: FilterType.MultiSelect,
    },
  ],
]);

export const ReturnFilters = new Map([
  ['courierId', { options: [], type: FilterType.MultiSelect }],
  ['receivedOn', { options: [], type: FilterType.DateRange }],
]);

export const HistoryTableColumns = new Map([
  ['changedOn', { label: 'App.StatusHistory.Table.ChangedOn', format: TableDataType.LongDate }],
  ['changedBy', { label: 'App.StatusHistory.Table.ChangedBy' }],
  ['status', { label: 'App.StatusHistory.Table.Status', isStatusColumn: true }],
]);

export const ReturnDisposeColumns = new Map([
  ['packageTrackingId', { label: 'App.Return.Table.TrackingNumber' }],
  ['accountNumber', { label: 'App.Return.Table.AccountNumber' }],
  ['customerName', { label: 'App.Return.Table.Name' }],
  ['carrierName', { label: 'App.Return.Table.Courier' }],
  ['packageStorageLocation', { label: 'App.Return.Table.StorageLocation', format: TableDataType.Text }],
  ['packageReceivedDate', { label: 'App.Return.Table.Datetime', format: TableDataType.ShortDate }],
  ['packageStatusId', { label: 'App.Return.Table.Status', isStatusColumn: true }],
]);

export const DeliverCustomerTableColumns = new Map([
  ['packageTrackingId', { label: 'App.Deliver.Table.TrackingID', format: TableDataType.Text }],
  ['carrierName', { label: 'App.Deliver.Table.Carrier', format: TableDataType.Text }],
  ['packageStorageLocation', { label: 'App.Deliver.Table.StorageLocation', format: TableDataType.Text }],
  ['accountNumber', { label: 'App.Deliver.Table.CustomerID', format: TableDataType.Text }],
  ['customerName', { label: 'App.Deliver.Table.CustomerName', format: TableDataType.Text }],
  ['packageReceivedDate', { label: 'App.Deliver.Table.CreatedOn', format: TableDataType.LongDate }],
  ['packageStatusId', { label: 'App.Deliver.Table.Status', isStatusColumn: true }],
]);

export const ReceiveHALPackagesTableColumns = new Map([
  ['packageTrackingId', { label: 'App.ReceivePkg.Table.TrackingID', format: TableDataType.Text }],
  ['carrierName', { label: 'App.ReceivePkg.Table.Courier', format: TableDataType.Text }],
  ['packageStorageLocation', { label: 'App.ReceivePkg.Table.StorageLocation', format: TableDataType.Text }],
  ['customerName', { label: 'App.ReceivePkg.Table.CustomerName', format: TableDataType.Text }],
  ['packageReceivedDate', { label: 'App.ReceivePkg.Table.PackageReceivedDate', format: TableDataType.LongDate }],
]);

export const ReceivePPUPackagesTableColumns = new Map([
  ['packageTrackingId', { label: 'App.ReceivePkg.Table.TrackingID', format: TableDataType.Text }],
  ['carrierName', { label: 'App.ReceivePkg.Table.Courier', format: TableDataType.Text }],
  ['packageStorageLocation', { label: 'App.ReceivePkg.Table.StorageLocation', format: TableDataType.Text }],
  ['accountNumber', { label: 'App.ReceivePkg.Table.AccountNumber', format: TableDataType.Text }],
  ['customerName', { label: 'App.ReceivePkg.Table.CustomerName', format: TableDataType.Text }],
  ['mobileNumber', { label: 'App.ReceivePkg.Table.MobileNumber', format: TableDataType.Mobile }],
  ['email', { label: 'App.ReceivePkg.Table.Email', format: TableDataType.Text }],
  ['packageReceivedDate', { label: 'App.ReceivePkg.Table.PackageReceivedDate', format: TableDataType.LongDate }],
]);

export const DeliverCustomerFilters = new Map([
  [
    'courierId',
    {
      options: [],
      type: FilterType.MultiSelect,
    },
  ],
  [
    'statusId',
    {
      options: [
        { id: 2, value: 'App.Status.received' },
        { id: 3, value: 'App.Status.receivedDamaged' },
        { id: 5, value: 'App.Status.picked' },
        { id: 6, value: 'App.Status.delivered' },
        { id: 7, value: 'App.Status.returned' },
        { id: 8, value: 'App.Status.refused' },
        { id: 9, value: 'App.Status.removed' },
        { id: 13, value: 'App.Status.receivedEmpty' },
      ],
      type: FilterType.MultiSelect,
    },
  ],
  [
    'orderTypeId',
    {
      options: [
        { id: 2, value: 'App.OrderType.subscription' },
        { id: 3, value: 'App.OrderType.hal' },
      ],
      type: FilterType.MultiSelect,
    },
  ],
  ['receivedOn', { options: [], type: FilterType.DateRange }],
]);

export const PickupConfirmationColumns = new Map([
  ['packageTrackingId', { label: 'App.Deliver.Table.TrackingID', format: TableDataType.Text }],
  ['carrierName', { label: 'App.Deliver.Table.Carrier', format: TableDataType.Text }],
  ['packageStorageLocation', { label: 'App.Deliver.Table.StorageLocation', format: TableDataType.Text }],
  ['accountNumber', { label: 'App.Deliver.Table.CustomerID', format: TableDataType.Text }],
  ['customerName', { label: 'App.Deliver.Table.CustomerName', format: TableDataType.Text }],
  ['packageReceivedDate', { label: 'App.Deliver.Table.CreatedOn', format: TableDataType.LongDate }],
  ['paymentStatusId', { label: 'App.Deliver.Table.Status', isStatusColumn: true }],
]);

export const SubscriptionHistoryTableColumns = new Map([
  ['transactionDate', { label: 'App.SubscriptionHistory.Table.PaidOn', format: TableDataType.StringDate }],
  ['payment', { label: 'App.SubscriptionHistory.Table.Payment', format: TableDataType.Text }],
  ['status', { label: 'App.SubscriptionHistory.Table.Status', format: TableDataType.Text }],
]);

// Admin Module
export const LOCATION_TYPE_LABELS: Record<number, string> = {
  [LocationType.PenguinPickup]: 'Penguin Pickup',
  [LocationType.PartnerShop]: 'Partner Shop',
  [LocationType.Locker]: 'Locker',
  [LocationType.PartnerCorporate]: 'Corporate Partner',
};

export const LocationTableColumns = new Map([
  ['locationNo', { label: 'App.Admin.Location.Table.StoreNum', format: TableDataType.Text }],
  ['locationName', { label: 'App.Admin.Location.Table.StoreName', format: TableDataType.Text }],
  ['locationTypeLabel', { label: 'App.Admin.Location.Table.LocationType', format: TableDataType.Text }],
  ['city', { label: 'App.Admin.Location.Table.City', format: TableDataType.Text }],
  ['provinceCode', { label: 'App.Admin.Location.Table.Province', format: TableDataType.Text }],
  ['startDate', { label: 'App.Admin.Location.Table.StartDate', format: TableDataType.Text }],
  ['statusId', { label: 'App.Admin.Location.Table.Status', isStatusColumn: true }],
]);

export const LocationFilters = new Map([
  [
    'provinceId',
    {
      type: FilterType.MultiSelect,
      options: [],
    },
  ],
  [
    'typeId',
    {
      type: FilterType.MultiSelect,
      options: [...Object.entries(LOCATION_TYPE_LABELS).map(([id, value]) => ({ id: +id, value }))],
    },
  ],
  // TODO - update to determined statuses
  [
    'statusId',
    {
      type: FilterType.MultiSelect,
      options: statusOptions(LOCATION_STATUS_META),
    },
  ],
  ['startDate', { options: [], type: FilterType.DateRange }],
]);

export const UserTableColumns = new Map([
  ['firstName', { label: 'App.Admin.User.Table.FirstName', format: TableDataType.Text }],
  ['lastName', { label: 'App.Admin.User.Table.LastName', format: TableDataType.Text }],
  ['email', { label: 'App.Admin.User.Table.Email', format: TableDataType.Text }],
  ['phoneNumber', { label: 'App.Admin.User.Table.PhoneNumber', format: TableDataType.Text }],
  ['roleName', { label: 'App.Admin.User.Table.RoleName', format: TableDataType.Text }],
  ['statusId', { label: 'App.Admin.User.Table.Status', isStatusColumn: true }],
]);

// Columns for the associated-users list shown on the Location edit page
export const LocationUserTableColumns = new Map([
  ['fullName', { label: 'App.Admin.User.Table.Name', format: TableDataType.Text }],
  ['email', { label: 'App.Admin.User.Table.Email', format: TableDataType.Text }],
  ['phoneNumber', { label: 'App.Admin.User.Table.PhoneNumber', format: TableDataType.Text }],
  ['roleName', { label: 'App.Admin.User.Table.RoleName', format: TableDataType.Text }],
  ['statusId', { label: 'App.Admin.User.Table.Status', isStatusColumn: true }],
]);

// Shared role options for filters and forms
export const USER_ROLE_OPTIONS = [
  { id: 1, value: 'Host Agent' },
  { id: 2, value: 'Employee' },
  { id: 3, value: 'CX User' },
  { id: 4, value: 'Admin' },
  { id: 5, value: 'Super Admin' },
  { id: 6, value: CORPORATE_ADMIN },
];

// Roles allowed when adding a new user
export const ADD_USER_ALLOWED_ROLES = ['Host Agent', 'Admin', 'Super Admin', 'CX User', CORPORATE_ADMIN];

// Roles allowed when adding a user from a location (Corporate User to follow)
export const LOCATION_ADD_USER_ALLOWED_ROLES = ['Host Agent'];

export const UserFilters = new Map([
  [
    'roleName',
    {
      type: FilterType.MultiSelect,
      options: USER_ROLE_OPTIONS,
    },
  ],
  [
    'isActive',
    {
      type: FilterType.MultiSelect,
      options: [
        { id: 1, value: 'App.Status.active' },
        { id: 0, value: 'App.Status.inactive' },
      ],
    },
  ],
  [
    'locationId',
    {
      type: FilterType.MultiSelect,
      options: [],
    },
  ],
]);

// Knowledge Center
export const KnowledgeTableColumns = new Map([
  ['materialTitle', { label: 'App.Admin.Knowledge.Table.Title', format: TableDataType.Text }],
  ['description', { label: 'App.Admin.Knowledge.Table.Description', format: TableDataType.Text }],
  ['categoryType', { label: 'App.Admin.Knowledge.Table.Category', format: TableDataType.Text }],
  ['modifiedOn', { label: 'App.Admin.Knowledge.Table.ModifiedOn', format: TableDataType.ShortDate }],
  ['isActive', { label: 'App.Admin.Knowledge.Table.Status', isStatusColumn: true }],
]);

export const KNOWLEDGE_CATEGORY_TYPE_OPTIONS = [
  { id: 1, value: 'App.Admin.Knowledge.Categories.CustomerDropOff' },
  { id: 2, value: 'App.Admin.Knowledge.Categories.DeliverToCustomer' },
  { id: 3, value: 'App.Admin.Knowledge.Categories.CourierDropOff' },
  { id: 4, value: 'App.Admin.Knowledge.Categories.ReturnToCourier' },
  { id: 5, value: 'App.Admin.Knowledge.Categories.Outbounds' },
  { id: 6, value: 'App.Admin.Knowledge.Categories.Printing' },
  { id: 7, value: 'App.Admin.Knowledge.Categories.Reporting' },
  { id: 8, value: 'App.Admin.Knowledge.Categories.Support' },
  { id: 9, value: 'App.Admin.Knowledge.Categories.Other' },
];

export const KnowledgeFilters = new Map([
  [
    'categoryTypeId',
    {
      type: FilterType.MultiSelect,
      options: KNOWLEDGE_CATEGORY_TYPE_OPTIONS,
    },
  ],
  [
    'statusId',
    {
      type: FilterType.MultiSelect,
      options: [
        { id: 1, value: 'App.Status.active' },
        { id: 0, value: 'App.Status.inactive' },
      ],
    },
  ],
  ['updatedDate', { options: [], type: FilterType.DateRange }],
]);

// Customers
export const CustomerTableColumns = new Map([
  ['generateCustomerId', { label: 'App.Admin.Customer.Table.PPUID', format: TableDataType.Text }],
  ['firstName', { label: 'App.Admin.Customer.Table.FirstName', format: TableDataType.Text }],
  ['lastName', { label: 'App.Admin.Customer.Table.LastName', format: TableDataType.Text }],
  ['email', { label: 'App.Admin.Customer.Table.Email', format: TableDataType.Text }],
  ['subscriptionTypeName', { label: 'App.Admin.Customer.Table.SubscriptionType', format: TableDataType.Text }],
  ['subscriptionStatus', { label: 'App.Admin.Customer.Table.SubscriptionStatus', isStatusColumn: true }],
  ['accountStatusId', { label: 'App.Admin.Customer.Table.AccountStatus', isStatusColumn: true }],
]);

// Customer Packages Table (for customer detail page)
export const CustomerPackageTableColumns = new Map([
  ['trackingId', { label: 'App.Admin.Customer.Table.TrackingNumber', format: TableDataType.Text }],
  ['courierName', { label: 'App.Admin.Customer.Table.Carrier', format: TableDataType.Text }],
  ['packageStatusId', { label: 'App.Admin.Customer.Table.Status', isStatusColumn: true }],
  ['receivedDate', { label: 'App.Admin.Customer.Table.Received', format: TableDataType.ShortDate }],
  ['pickupDate', { label: 'App.Admin.Customer.Table.Pickup', format: TableDataType.ShortDate }],
]);

// cityProv is composed by VolumeTableComponent.
export const VolumeTableColumns = new Map<string, TableColumn>([
  ['name', { label: 'App.Admin.Dashboard.Table.PartnerShop', sortable: true }],
  ['cityProv', { label: 'App.Admin.Dashboard.Table.CityProv' }],
  [
    'startDate',
    {
      label: 'App.Admin.Dashboard.Table.StartDate',
      format: TableDataType.StringDate,
      sortable: true,
      dividerAfter: true,
    },
  ],
  ['hal', { label: 'App.Charts.hal', sortable: true, numeric: true }],
  ['subscription', { label: 'App.Charts.subscription', sortable: true, numeric: true }],
  ['dropOff', { label: 'App.Charts.dropOff', sortable: true, numeric: true }],
  ['outbound', { label: 'App.Charts.outbound', sortable: true, numeric: true }],
  ['total', { label: 'App.Admin.Dashboard.Table.Total', sortable: true, numeric: true, dividerAfter: true }],
  ['priorTotal', { label: 'App.Admin.Dashboard.Table.PriorTotal', sortable: true, numeric: true }],
  ['diffAbsolute', { label: 'App.Admin.Dashboard.Table.Diff', sortable: true, numeric: true }],
]);

export const CommissionEstimateTableColumns = new Map<string, TableColumn>([
  ['storeName', { label: 'App.Admin.Dashboard.Commission.PartnerShop', sortable: true }],
  ['cityProv', { label: 'App.Admin.Dashboard.Table.CityProv' }],
  [
    'preTaxAmount',
    { label: 'App.Admin.Dashboard.Commission.PreTax', sortable: true, numeric: true, dividerAfter: true },
  ],
  ['taxAmount', { label: 'App.Admin.Dashboard.Commission.Tax', sortable: true, numeric: true }],
  ['postTaxAmount', { label: 'App.Admin.Dashboard.Commission.EstimatedCommission', sortable: true, numeric: true }],
]);

export const FinancialOverviewTableColumns = new Map<string, TableColumn>([
  ['storeName',             { label: 'App.Admin.Dashboard.Commission.PartnerShop', sortable: true }],
  ['cityProv',              { label: 'App.Admin.Dashboard.Table.CityProv' }],
  ['totalRevenue',          { label: 'App.Admin.Dashboard.Revenue.CourierRevenue', sortable: true, numeric: true, dividerAfter: true }],
  ['payoutPreTaxAmount',    { label: 'App.Admin.Dashboard.Revenue.PayoutPreTax', sortable: true, numeric: true }],
  ['payoutTaxAmount',       { label: 'App.Admin.Dashboard.Revenue.PayoutTax', sortable: true, numeric: true }],
  ['payoutPostTaxAmount',   { label: 'App.Admin.Dashboard.Commission.EstimatedPayout', sortable: true, numeric: true }],
]);

// Subscription type options derived from StatusMeta
export const SUBSCRIPTION_TYPE_OPTIONS = statusOptions(SUBSCRIPTION_TYPE_META);

// Customer List Filters
export const CustomerFilters = new Map([
  [
    'subscriptionType',
    {
      type: FilterType.MultiSelect,
      options: statusOptions(SUBSCRIPTION_TYPE_META),
    },
  ],
  [
    'subscriptionStatus',
    {
      type: FilterType.MultiSelect,
      options: statusOptionsString(SUBSCRIPTION_STATUS_META),
    },
  ],
  [
    'accountStatusId',
    {
      type: FilterType.MultiSelect,
      options: statusOptions(ACCOUNT_STATUS_META),
    },
  ],
]);

// Customer Detail - Package Filters
export const CustomerPackageFilters = new Map([
  [
    'statusId',
    {
      type: FilterType.MultiSelect,
      options: statusOptions(PACKAGE_STATUS_META),
    },
  ],
  [
    'courierId',
    {
      type: FilterType.MultiSelect,
      options: [], // populated dynamically from courier list
    },
  ],
  ['receivedOn', { type: FilterType.DateRange, options: [] }],
  ['pickupOn', { type: FilterType.DateRange, options: [] }],
]);

// Shipping History
export const ShippingHistoryTableColumns = new Map([
  ['createdOn', { label: 'App.ShippingTool.ShippingHistory.Table.CreatedOn', format: TableDataType.LongDate }],
  [
    'trackingId',
    {
      label: 'App.ShippingTool.ShippingHistory.Table.TrackingId',
      format: TableDataType.Text,
      tooltip: 'App.ShippingTool.ShippingHistory.Table.TrackingIdTooltip',
    },
  ],
  ['sender', { label: 'App.ShippingTool.ShippingHistory.Table.Sender', format: TableDataType.Text }],
  ['recipient', { label: 'App.ShippingTool.ShippingHistory.Table.Recipient', format: TableDataType.Text }],
  ['courier', { label: 'App.ShippingTool.ShippingHistory.Table.Courier', format: TableDataType.Text }],
  ['statusId', { label: 'App.ShippingTool.ShippingHistory.Table.Status', isStatusColumn: true }],
]);

export const ShippingHistoryFilters = new Map([
  [
    'statusId',
    {
      type: FilterType.MultiSelect,
      options: statusOptions(SHIPMENT_STATUS_META).filter((s) => s.id !== 5),
    },
  ],
  ['createdOn', { options: [], type: FilterType.DateRange }],
]);

// Chart Configuration
export const CHART_CONFIG = {
  XAxis: true,
  YAxis: true,
  autoScale: true,
  showGridLines: false,
  animations: true,
  roundEdges: false,
  barPadding: 4,
};

export const LINE_CHART_SCHEME = {
  name: 'custom-line',
  selectable: true,
  group: ScaleType.Ordinal,
  domain: ['#0000FF'], // electric-blue
};

export const BAR_CHART_SCHEME = {
  name: 'custom-bar',
  selectable: true,
  group: ScaleType.Ordinal,
  domain: ['#0000FF', '#B4F9FF'], // electric-blue, sky-blue
};

export const VOLUME_CHART_SCHEME = {
  name: 'volume-stacked',
  selectable: true,
  group: ScaleType.Ordinal,
  // Series order: Drop-off, HAL, Subscription, Outbound.
  domain: ['#0000FF', '#00C7FF', '#FFC800', '#FF7F00'],
};

export const REASON_FOR_EXPORT_OPTIONS = [
  { id: 'GIFT', value: 'App.ShippingTool.CustomsForm.Labels.Gift' },
  { id: 'COM_SAMPLE', value: 'App.ShippingTool.CustomsForm.Labels.CommercialSample' },
  { id: 'COM_SALE', value: 'App.ShippingTool.CustomsForm.Labels.CommercialSale' },
  { id: 'REPAIR', value: 'App.ShippingTool.CustomsForm.Labels.Repair' },
  { id: 'PERSONAL', value: 'App.ShippingTool.CustomsForm.Labels.Personal' },
  { id: 'RETURN', value: 'App.ShippingTool.CustomsForm.Labels.Return' },
  { id: 'OTH', value: 'App.ShippingTool.CustomsForm.Labels.Other' },
];

export const TERMS_OF_TRADE_OPTIONS = [{ id: 'DAP', value: 'App.ShippingTool.CustomsForm.Labels.DAP' }];

export const CUSTOMS_CURRENCY_OPTIONS = [
  { id: 'USD', value: 'USD' },
  { id: 'CAD', value: 'CAD' },
];

export const AdminDropoffTableColumns = new Map([
  ['trackingId', { label: 'App.Admin.Package.Table.TrackingId', format: TableDataType.Text }],
  ['courierName', { label: 'App.Admin.Package.Table.Courier', format: TableDataType.Text }],
  ['receivedDate', { label: 'App.Admin.Package.Table.ReceivedDate', format: TableDataType.LongDate }],
  ['location', { label: 'App.Admin.Package.Table.Location', format: TableDataType.Text }],
]);

export const AdminDropoffFilters = new Map([
  [
    'courier',
    {
      options: dropoffCourierOptions,
      type: FilterType.MultiSelect,
    },
  ],
  [
    'locationId',
    {
      type: FilterType.MultiSelect,
      options: [],
    },
  ],
  ['createdOn', { options: [], type: FilterType.DateRange }],
]);

export const AdminPackageTableColumns = new Map([
  ['trackingId', { label: 'App.Admin.Package.Table.TrackingId', format: TableDataType.Text }],
  ['courierName', { label: 'App.Admin.Package.Table.Courier', format: TableDataType.Text }],
  ['storageLocation', { label: 'App.Admin.Package.Table.StorageLocation', format: TableDataType.Text }],
  ['customerAccountNumber', { label: 'App.Admin.Package.Table.CustomerAccountNumber', format: TableDataType.Text }],
  ['customerFullName', { label: 'App.Admin.Package.Table.CustomerFullName', format: TableDataType.Text }],
  ['receivedDate', { label: 'App.Admin.Package.Table.ReceivedDate', format: TableDataType.LongDate }],
  ['pickupDate', { label: 'App.Admin.Package.Table.PickupDate', format: TableDataType.LongDate }],
  ['location', { label: 'App.Admin.Package.Table.Location', format: TableDataType.Text }],
  ['packageStatusId', { label: 'App.Admin.Package.Table.Status', isStatusColumn: true }],
]);

export const AdminPackageFilters = new Map([
  [
    'courierId',
    {
      type: FilterType.MultiSelect,
      options: [],
    },
  ],
  [
    'statusId',
    {
      options: [
        { id: 2, value: 'App.Status.received' },
        { id: 3, value: 'App.Status.receivedDamaged' },
        { id: 5, value: 'App.Status.picked' },
        { id: 6, value: 'App.Status.delivered' },
        { id: 7, value: 'App.Status.returned' },
        { id: 8, value: 'App.Status.refused' },
        { id: 9, value: 'App.Status.removed' },
        { id: 13, value: 'App.Status.receivedEmpty' },
      ],
      type: FilterType.MultiSelect,
    },
  ],
  [
    'orderTypeId',
    {
      options: [
        { id: 2, value: 'App.OrderType.subscription' },
        { id: 3, value: 'App.OrderType.hal' },
      ],
      type: FilterType.MultiSelect,
    },
  ],
  [
    'locationId',
    {
      type: FilterType.MultiSelect,
      options: [],
    },
  ],
  ['receivedOn', { options: [], type: FilterType.DateRange }],
  ['pickupOn', { options: [], type: FilterType.DateRange }],
]);

export const returnLabelProviders = ['canadapost', 'icscourier', 'purolator', 'fedex', 'ups'];
export const signatureNotSupportedProviders = ['icscourier'];

export const LeadTableColumns = new Map([
  ['companyName', { label: 'App.Admin.Lead.Table.CompanyName', format: TableDataType.Text }],
  ['businessAddress', { label: 'App.Admin.Lead.Table.BusinessAddress', format: TableDataType.Text }],
  ['city', { label: 'App.Admin.Lead.Table.City', format: TableDataType.Text }],
  ['provinceCode', { label: 'App.Admin.Lead.Table.Province', format: TableDataType.Text }],
  ['emailAddress', { label: 'App.Admin.Lead.Table.EmailAddress', format: TableDataType.Text }],
  ['capacityRange', { label: 'App.Admin.Lead.Table.CapacityRange', format: TableDataType.Text }],
  ['createdOn', { label: 'App.Admin.Lead.Table.SubmittedOn', format: TableDataType.LongDate }],
  ['leadStatusId', { label: 'App.Admin.Lead.Table.Status', isStatusColumn: true }],
]);

export const LeadFilters = new Map([
  [
    'provinceId',
    {
      type: FilterType.MultiSelect,
      options: [],
    },
  ],
  [
    'businessTypeId',
    {
      type: FilterType.MultiSelect,
      options: statusOptions(BUSINESS_TYPE_META),
    },
  ],
  [
    'capacityRangeId',
    {
      type: FilterType.MultiSelect,
      options: statusOptions(CAPACITY_RANGE_META),
    },
  ],
  [
    'statusId',
    {
      type: FilterType.MultiSelect,
      options: statusOptions(LEAD_STATUS_META),
    },
  ],
  ['submittedOn', { options: [], type: FilterType.DateRange }],
]);
